import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

import {
  ArrowRight,
  Calendar,
  CheckCircle,
  ChartLine,
  GearSix,
  Lightning,
  Lock,
  Megaphone,
  Target,
  UsersThree
} from '@phosphor-icons/react';

const AIAgentsPage = () => {
  const { authStatus, user } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  return (
    <>
      <Helmet>
        <title>Build Your AI Workforce | Man & Machine</title>
        <meta name="description" content="Create AI workers that handle leads, bookings, follow-ups, and admin automatically. No coding required. Set up workers in minutes." />
        <meta name="keywords" content="AI workers, business automation, AI workforce, workflow automation, no-code AI, service business" />
        <link rel="canonical" href="https://manandmachine.ai/ai-agents" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://manandmachine.ai/ai-agents" />
        <meta property="og:title" content="Build Your AI Workforce | Man & Machine" />
        <meta property="og:description" content="Create AI workers that handle leads, bookings, follow-ups, and admin automatically. No coding required." />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Build Your AI Workforce | Man & Machine" />
        <meta name="twitter:description" content="Create AI workers that handle leads, bookings, follow-ups, and admin automatically. No coding required." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Man & Machine AI Workers",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "Create AI workers that handle leads, bookings, follow-ups, and admin for your business automatically.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "AUD"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Man & Machine",
              "url": "https://manandmachine.ai"
            }
          })}
        </script>
      </Helmet>

      <PublicNavHeader />

      <div className="min-h-screen bg-[#161B26]">

        {/* ═══ HERO SECTION ═══ */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,229,192,0.06)] via-transparent to-[rgba(0,229,192,0.03)]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] sm:w-[800px] h-[300px] sm:h-[400px] bg-[rgba(0,229,192,0.08)] blur-[120px] rounded-full" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="text-center max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
                <Lightning size={14} className="text-[#00E5C0]" />
                <span className="text-xs text-[#A0AEC0]">AI Workers</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Build Your AI Workforce
              </h1>

              <p className="text-base sm:text-lg text-[#A0AEC0] mb-3 px-2 max-w-2xl mx-auto leading-relaxed">
                Create AI workers that handle leads, bookings, follow-ups, and admin - automatically.
              </p>

              <p className="text-sm text-[#7D8896] px-2">
                No coding required. Set up workers in minutes and let them run your operations in the background.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ═══ TYPES OF WORKERS ═══ */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-2xl sm:text-3xl font-bold text-white mb-8 sm:mb-10 text-center"
          >
            Types of <span className="text-[#00E5C0]">Workers</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Target,
                title: 'Lead & Sales Workers',
                desc: 'Handle enquiries, respond instantly, and convert leads into jobs.',
                color: '#00E5C0'
              },
              {
                icon: Calendar,
                title: 'Operations Workers',
                desc: 'Manage bookings, scheduling, and day-to-day admin tasks.',
                color: '#EC4899'
              },
              {
                icon: Megaphone,
                title: 'Communication Workers',
                desc: 'Send updates, follow-ups, reminders, and customer messages automatically.',
                color: '#F59E0B'
              }
            ].map((type, i) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.25)] transition-colors"
                data-testid={`worker-type-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${type.color}15` }}
                >
                  <type.icon size={24} style={{ color: type.color }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{type.title}</h3>
                <p className="text-[#A0AEC0] text-sm">{type.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══ HOW WORKERS OPERATE ═══ */}
        <section className="py-16 sm:py-24 bg-[rgba(255,255,255,0.02)]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-2xl sm:text-3xl font-bold text-white mb-10 sm:mb-14 text-center"
            >
              How Workers <span className="text-[#00E5C0]">Operate</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  num: '1',
                  title: 'A trigger happens',
                  desc: 'A new enquiry, message, or event',
                  color: '#00E5C0'
                },
                {
                  num: '2',
                  title: 'The worker responds instantly',
                  desc: 'Handles the task based on your setup',
                  color: '#EC4899'
                },
                {
                  num: '3',
                  title: 'The work gets done automatically',
                  desc: 'Leads are answered, jobs are booked, follow-ups are sent',
                  color: '#F59E0B'
                }
              ].map((step, i) => (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: i * 0.12 }}
                  className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] text-center"
                  data-testid={`how-step-${step.num}`}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${step.color}15` }}
                  >
                    <span className="font-bold text-lg" style={{ color: step.color }}>{step.num}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-[#A0AEC0] text-sm">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ CREATE WORKERS IN MINUTES ═══ */}
        <section className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-center mb-10"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Create Workers in <span className="text-[#00E5C0]">Minutes</span>
              </h2>
              <p className="text-[#A0AEC0] max-w-xl mx-auto">
                Describe what you want the worker to do, and the system sets it up for you.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass rounded-2xl p-7 border border-[rgba(0,229,192,0.15)]"
              data-testid="create-workers-section"
            >
              <div className="flex items-center gap-2 mb-5">
                <Lightning size={20} className="text-[#00E5C0]" />
                <h3 className="text-lg font-semibold text-white">Just describe the outcome</h3>
              </div>

              <div className="space-y-3">
                {[
                  '"Respond to new enquiries instantly"',
                  '"Book jobs into my calendar"',
                  '"Follow up leads after 24 hours"'
                ].map((example, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-xl border border-[rgba(255,255,255,0.05)]">
                    <CheckCircle size={16} className="text-[#00E5C0] flex-shrink-0" />
                    <span className="text-[#E2E8F0] text-sm italic">{example}</span>
                  </div>
                ))}
              </div>

              <p className="text-[#7D8896] text-sm mt-5">
                The system handles the configuration. You focus on the results.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ═══ POSITIONING LINE ═══ */}
        <section className="py-12 sm:py-16 bg-[rgba(255,255,255,0.02)]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-xl sm:text-2xl font-bold text-center text-white leading-snug"
            >
              Your business doesn't need more software -<br />
              <span className="text-[#00E5C0]">it needs workers that get things done.</span>
            </motion.p>
          </div>
        </section>

        {/* ═══ BUILT FOR TEAMS AND BUSINESSES ═══ */}
        <section className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center"
            >
              Built for <span className="text-[#00E5C0]">Teams and Businesses</span>
            </motion.h2>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Lock, text: 'Control who can access and manage workers' },
                { icon: UsersThree, text: 'Assign workers to different roles or teams' },
                { icon: ChartLine, text: 'Track activity and performance' },
                { icon: GearSix, text: 'Set rules and limits where needed' }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: i * 0.08 }}
                  className="flex items-center gap-3 p-4 glass rounded-xl border border-[rgba(255,255,255,0.05)]"
                  data-testid={`governance-item-${i}`}
                >
                  <item.icon size={20} className="text-[#00E5C0] flex-shrink-0" />
                  <span className="text-[#E2E8F0] text-sm">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ FINAL CTA ═══ */}
        <section className="py-16 sm:py-24 bg-[rgba(255,255,255,0.02)]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass rounded-2xl p-8 sm:p-10 text-center border border-[rgba(0,229,192,0.15)]"
              data-testid="final-cta-section"
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                Start Building Your{' '}
                <span className="text-[#00E5C0]">AI Workforce</span>
              </h2>
              <p className="text-[#A0AEC0] mb-8 max-w-xl mx-auto">
                Create workers that handle your operations so you can focus on growing your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Link
                  to={isAuthenticated ? "/workers/new" : "/register"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)] min-h-[48px]"
                  data-testid="cta-create-worker"
                >
                  Create a Worker
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all min-h-[48px]"
                  data-testid="cta-book-demo"
                >
                  Book a Demo
                </Link>
              </div>

              <p className="text-sm text-[#7D8896] mt-5">
                No credit card required. Start in under 2 minutes.
              </p>
            </motion.div>
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
};

export default AIAgentsPage;
